import { AxiosError } from 'axios';
import { Outlet, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useEffect, useState } from 'react';

import Header from '../Header';
import Footer from '../Footer';
import OngoingEvaluations from '@/components/OngoingEvaluations';

import { Iconlabel } from '@wk/components-react16';

import { getUserRoleAccess } from '@/services';
import { isReleaseNotesEnabledBySwagger } from '@/services/conformance';
import useOnGoingEvaluationsStore from '@/stores/ongoingEvaluations';
import useApplicationsStore from '@/stores/applications';
import useToast from '@/stores/toast';
import Toaster from '@/components/ui/Toaster';
import type { ErrorResponse } from '@/types';

export type AppLayoutOutletContext = {
  isReleaseNotesAvailable: boolean;
};

const AppLayout = () => {
  const location = useLocation();
  const isApplicationsRoute = location.pathname.startsWith('/applications');
  const isEvaluationsRoute = location.pathname.startsWith('/evaluations');
  const isSettingsRoute = location.pathname.startsWith('/settings');
  const isNewsletterRoute = location.pathname.startsWith('/newsletter');
  const shouldUseMobileDocumentFlow =
    isApplicationsRoute ||
    isEvaluationsRoute ||
    isSettingsRoute ||
    isNewsletterRoute;

  const { isLoading, error: applicationsError, fetchApplications } = useApplicationsStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      error: state.error,
      fetchApplications: state.fetchApplications,
    })),
  );

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const { isLoading: isUserRoleAccessLoading } = useQuery({
    queryKey: ['userRoleAccess'],
    queryFn: getUserRoleAccess,
  });
  const {
    isLoading: isReleaseNotesAvailabilityLoading,
    data: isReleaseNotesAvailable = false,
  } = useQuery({
    queryKey: ['release-notes', 'availability'],
    queryFn: isReleaseNotesEnabledBySwagger,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isInitialLayoutLoading =
    isLoading || isUserRoleAccessLoading || isReleaseNotesAvailabilityLoading;
  const [isRouteContentLoading, setIsRouteContentLoading] = useState(false);

  const { isOngoingEvaluationsOpen, setIsOngoingEvaluationsOpen } =
    useOnGoingEvaluationsStore(
      useShallow((state) => ({
        isOngoingEvaluationsOpen: state.isOngoingEvaluationsOpen,
        setIsOngoingEvaluationsOpen: state.setIsOngoingEvaluationsOpen,
      })),
    );
  const { clearToasts } = useToast(
    useShallow((state) => ({
      clearToasts: state.clearToasts,
    })),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRouteLoading = (
      event: Event,
    ) => {
      const customEvent = event as CustomEvent<{ loading?: boolean }>;
      setIsRouteContentLoading(Boolean(customEvent.detail?.loading));
    };

    window.addEventListener(
      'app-layout:mobile-route-loading',
      handleRouteLoading as EventListener,
    );

    return () => {
      window.removeEventListener(
        'app-layout:mobile-route-loading',
        handleRouteLoading as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    const shouldAwaitRouteLoadingSignal =
      location.pathname === '/applications' ||
      (location.pathname.startsWith('/applications/') &&
        !location.pathname.startsWith('/applications/onboard')) ||
      location.pathname.startsWith('/evaluations');

    setIsRouteContentLoading(shouldAwaitRouteLoadingSignal);
  }, [location.pathname]);

  useEffect(() => {
    clearToasts();
  }, [clearToasts, location.pathname, location.search]);

  const isFooterSuppressedOnMobile =
    shouldUseMobileDocumentFlow &&
    (isInitialLayoutLoading || isRouteContentLoading);

  const renderContent = () => {
    if (applicationsError) {
      const appError = applicationsError as AxiosError<ErrorResponse>;
      return (
        <div className="w-full h-full justify-center items-center flex flex-col gap-2">
          <h2 className="text-lg font-bold">Error</h2>
          <p className="text-sm text-gray-500">
            {appError?.response?.data.title ||
              'An error occurred while fetching application data.'}
          </p>
        </div>
      );
    }

    if (isInitialLayoutLoading) {
      return (
        <div className="flex w-full flex-1 min-h-[55vh] items-center justify-center">
          <span className="animate-spin">
            <Iconlabel name="spinner-alt" />
          </span>
        </div>
      );
    }
    return <Outlet context={{ isReleaseNotesAvailable }} />;
  };

  return (
    <div
      className={`h-full w-full flex flex-col relative ${
        shouldUseMobileDocumentFlow
          ? 'max-sm:h-auto max-sm:min-h-dvh max-sm:overflow-x-hidden'
          : 'max-sm:overflow-x-hidden'
      }`}
    >
      <Header />
      <div
        className="flex flex-col gap-4 px-4 lg:pr-8 py-4 w-full min-h-0 flex-1"
      >
        {renderContent()}
        <OngoingEvaluations
          isOpen={isOngoingEvaluationsOpen}
          onClose={() => setIsOngoingEvaluationsOpen(false)}
        />
        <Toaster />
      </div>
      <div
        className={
          isFooterSuppressedOnMobile ? 'max-sm:hidden' : ''
        }
      >
        <Footer isReleaseNotesAvailable={isReleaseNotesAvailable} />
      </div>
    </div>
  );
};

export default AppLayout;
